import { useState, useEffect, useContext, ReactNode, useRef } from 'react';
import { TransactionValidationDialog } from '../components/shared/dialogs/TransactionValidationDialog';
import { TransactionValidationResponse, TransactionValidationConflict } from '../app/services/api/transactionValidationTypes';
import { TransactionValidationMode } from '../enums/TransactionValidationMode';
import { ConfigurationName } from '../enums/ConfigurationName';
import { useConfigurations } from '../components/shared/useConfigurations';
import { UserContext } from '../components/shared/useUser';
import useLogError from './useLogError';

export const useTransactionValidationGuard = () => {
  const { selectedStore } = useContext(UserContext);
  const { getConfigurationValue } = useConfigurations();
  const { logError } = useLogError();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'Warn' | 'Block'>('Warn');
  const [conflicts, setConflicts] = useState<TransactionValidationConflict[]>([]);
  const [pendingProceed, setPendingProceed] = useState<(() => void) | null>(null);
  const isValidationInFlight = useRef(false);
  const validationGeneration = useRef(0);
  const currentStoreNumber = useRef(selectedStore?.storeNumber);
  currentStoreNumber.current = selectedStore?.storeNumber;
  const configurationValue = getConfigurationValue(ConfigurationName.TransactionValidation)?.toLowerCase();
  const isGuardEnabled = configurationValue === TransactionValidationMode.Warn.toLowerCase()
    || configurationValue === TransactionValidationMode.Block.toLowerCase();

  useEffect(() => {
    validationGeneration.current += 1;
    setOpen(false);
    setPendingProceed(null);
    isValidationInFlight.current = false;
  }, [selectedStore?.storeNumber]);

  const runGuard = async (check: () => Promise<TransactionValidationResponse>, onProceed: () => void) => {
    if (isValidationInFlight.current) {
      return;
    }

    if (isGuardEnabled === false || !selectedStore?.storeNumber) {
      onProceed();
      return;
    }
    const storeNumber = selectedStore.storeNumber;
    const requestGeneration = validationGeneration.current;
    isValidationInFlight.current = true;
    const keepLockForDialog = { current: false };
    try {
      const result = (await check())?.data;

      if (currentStoreNumber.current !== storeNumber || validationGeneration.current !== requestGeneration) {
        return;
      }

      if (result?.shouldBlock) {
        keepLockForDialog.current = true;
        setMode('Block');
        setConflicts(result.conflicts ?? []);
        setPendingProceed(null);
        setOpen(true);
        return;
      }

      if (result?.shouldWarn) {
        keepLockForDialog.current = true;
        setMode('Warn');
        setConflicts(result.conflicts ?? []);
        setPendingProceed(() => onProceed);
        setOpen(true);
        return;
      }

      onProceed();
    } catch (error) {
      if (currentStoreNumber.current === storeNumber) {
        logError(error);
        onProceed();
      }
    } finally {
      if (!keepLockForDialog.current && validationGeneration.current === requestGeneration) {
        isValidationInFlight.current = false;
      }
    }
  };

  const handleContinue = () => {
    setOpen(false);
    isValidationInFlight.current = false;
    const proceed = pendingProceed;
    setPendingProceed(null);
    if (proceed) {
      proceed();
    }
  };

  const handleCancel = () => {
    setOpen(false);
    isValidationInFlight.current = false;
    setPendingProceed(null);
  };

  const transactionValidationDialog: ReactNode = (
    <TransactionValidationDialog
      open={open}
      mode={mode}
      conflicts={conflicts}
      onContinue={handleContinue}
      onCancel={handleCancel}
    />
  );

  return { runGuard, transactionValidationDialog };
};
